<template>
  <div
    v-if="hasQuotes"
    class="quote-carousel"
    :style="cssVars"
    aria-live="polite"
  >
    <transition name="quote-fade" mode="out-in">
      <blockquote :key="currentIndex" class="quote-carousel__item">
        <slot :quote="currentQuote" :index="currentIndex">
          <p class="quote-carousel__text">{{ currentQuote.text }}</p>
          <cite v-if="currentQuote.reference" class="quote-carousel__author">
            - {{ currentQuote.reference }}
          </cite>
        </slot>
      </blockquote>
    </transition>
  </div>
</template>

<script>
export default {
  name: 'QuoteCarousel',
  props: {
    quotes: {
      type: Array,
      required: true,
      validator: (value) => value.every((q) => q && typeof q.text === 'string'),
    },
    intervalMs: {
      type: Number,
      default: 5000,
      validator: (value) => value > 0,
    },
    fadeMs: {
      type: Number,
      default: 500,
      validator: (value) => value >= 0,
    },
    startIndex: {
      type: Number,
      default: 0,
    },
    autoplay: {
      type: Boolean,
      default: true,
    },
    shuffle: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      currentIndex: 0,
      timerId: null,
    }
  },
  computed: {
    hasQuotes() {
      return Array.isArray(this.quotes) && this.quotes.length > 0
    },
    canRotate() {
      return this.hasQuotes && this.quotes.length > 1 && this.autoplay
    },
    currentQuote() {
      return this.quotes[this.currentIndex] || { text: '', reference: '' }
    },
    cssVars() {
      return { '--quote-fade-duration': `${this.fadeMs}ms` }
    },
  },
  watch: {
    quotes() {
      this.resetCarousel()
    },
    autoplay(value) {
      value ? this.start() : this.stop()
    },
    intervalMs() {
      if (this.canRotate) {
        this.stop()
        this.start()
      }
    },
  },
  mounted() {
    this.currentIndex = this.normalizeIndex(this.startIndex)
    if (this.shuffle) this.shuffleQuotes()
    this.start()
  },
  beforeDestroy() {
    this.stop()
  },
  methods: {
    start() {
      if (!this.canRotate || this.timerId) return
      this.timerId = setInterval(this.next, this.intervalMs)
    },
    stop() {
      if (!this.timerId) return
      clearInterval(this.timerId)
      this.timerId = null
    },
    next() {
      this.currentIndex = (this.currentIndex + 1) % this.quotes.length
      this.$emit('change', { index: this.currentIndex, quote: this.currentQuote })
    },
    resetCarousel() {
      this.stop()
      this.currentIndex = this.normalizeIndex(this.startIndex)
      this.start()
    },
    normalizeIndex(index) {
      if (!this.hasQuotes) return 0
      const len = this.quotes.length
      return ((index % len) + len) % len
    },
    shuffleQuotes() {
      this.currentIndex = Math.floor(Math.random() * this.quotes.length)
    },
  },
}
</script>

<style scoped>
.quote-carousel {
  position: relative;
}

.quote-carousel__item {
  margin: 0;
  padding: 0;
  border: 0;
}

.quote-carousel__text {
  margin: 0 0 1rem;
  font-family: 'Playfair Display', 'Cormorant Garamond', Georgia, serif;
  font-size: 2.4rem;
  font-weight: 600;
  line-height: 1.3;
  letter-spacing: -0.01em;
  color: inherit;
}

.quote-carousel__text::before {
  content: '\201C';
  display: inline-block;
  margin-right: 0.15em;
  font-size: 1em;
  line-height: 0;
  vertical-align: -0.15em;
  opacity: 0.55;
}

.quote-carousel__author {
  display: block;
  margin-top: 0.5rem;
  font-family: 'Playfair Display', Georgia, serif;
  font-size: 1rem;
  font-style: italic;
  font-weight: 500;
  letter-spacing: 0.02em;
  opacity: 0.8;
}

.quote-fade-enter-active,
.quote-fade-leave-active {
  transition: opacity var(--quote-fade-duration, 500ms) ease-in-out;
}

.quote-fade-enter,
.quote-fade-leave-to {
  opacity: 0;
}
</style>
